import { Form } from 'formik';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikTextField,
  FormikNumericTextField,
  P2PProviderSelect,
  ShopSelect,
  TraderSelect,
  FormikDialog,
  FormikCheckbox,
  FormikSelect,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { TelegramFlow, TelegramUser } from 'types';
import { formUtils } from 'utils';

const DEPENDENT_CHECKBOX_INDENT = 16;

export enum TelegramFlowContext {
  Trader = 'trader',
  Shop = 'shop',
  P2PProvider = 'p2p-provider',
}

// Default templates live in the backend (telegram-flow.contants.ts); the helpers repeat them.
const DEFAULT_DISPUTE_TEMPLATE_HELPERS = {
  [TelegramFlowContext.Trader]: 'fields.default_template_trader_helper',
  [TelegramFlowContext.P2PProvider]:
    'fields.default_template_p2p_provider_helper',
  [TelegramFlowContext.Shop]: 'fields.default_template_shop_helper',
} as const;

const renderMultiline = (text: string) => (
  <span className="tw-whitespace-pre-line">{text}</span>
);

type Values = {
  traderId?: string;
  shopId?: string;
  p2pProviderId?: string;
  telegramUserId?: string;
  chatId: string;
  enabled: boolean;
  payinDisputeTemplate: string | null;
  defaultPayinDisputeTemplate?: boolean;
  payinCancelTemplate: string | null;
  defaultPayinCancelTemplate?: boolean;
  payinCompleteTemplate: string | null;
  defaultPayinCompleteTemplate?: boolean;
  payinRequireReceipt: boolean;
  payinHideActions: boolean;
  payinHideCancel: boolean;
  payinSendDisputesMessage: boolean;
};

type Props = DialogProps<CloseFormikDialogResult<Values>> & {
  context: TelegramFlowContext;
  telegramUsers: TelegramUser[];
};

export const TelegramFlowDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  context,
  telegramUsers,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow.dialog',
  });
  const { t: tCommon } = useTranslation();

  const hideActionsPaddingLeft = useMemo(
    () =>
      context === TelegramFlowContext.Shop ? DEPENDENT_CHECKBOX_INDENT : 0,
    [context],
  );

  const title = useMemo(
    () => (data ? t('edit_title') : t('create_title')),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      traderId: '',
      shopId: '',
      p2pProviderId: '',
      telegramUserId: '',
      chatId: '',
      enabled: true,
      payinDisputeTemplate: '',
      defaultPayinDisputeTemplate: true,
      payinCancelTemplate: '',
      defaultPayinCancelTemplate: true,
      payinCompleteTemplate: '',
      defaultPayinCompleteTemplate: true,
      payinHideActions: false,
      payinHideCancel: false,
      payinRequireReceipt: false,
      payinSendDisputesMessage: false,
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const telegramUsersOptions = useMemo(
    () => formUtils.getOptions(telegramUsers),
    [telegramUsers],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        traderId:
          context === TelegramFlowContext.Trader
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        shopId:
          context === TelegramFlowContext.Shop
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        p2pProviderId:
          context === TelegramFlowContext.P2PProvider
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        telegramUserId: Yup.string().nullable().optional(),
        chatId: Yup.string().required(tCommon('errors.required')),
        enabled: Yup.boolean(),
        defaultPayinDisputeTemplate: Yup.boolean(),
        defaultPayinCancelTemplate: Yup.boolean(),
        defaultPayinCompleteTemplate: Yup.boolean(),
        payinHideActions: Yup.boolean(),
        payinHideCancel: Yup.boolean(),
        payinSendDisputesMessage: Yup.boolean(),
        payinRequireReceipt:
          context === TelegramFlowContext.Shop
            ? Yup.boolean()
            : Yup.boolean().nullable().optional(),
        payinDisputeTemplate: Yup.string().when('defaultPayinDisputeTemplate', {
          is: false,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        payinCancelTemplate: Yup.string().when('defaultPayinCancelTemplate', {
          is: false,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        payinCompleteTemplate: Yup.string().when(
          'defaultPayinCompleteTemplate',
          {
            is: false,
            then: (schema) => schema.required(tCommon('errors.required')),
            otherwise: (schema) => schema.notRequired(),
          },
        ),
      }),
    [context, tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    const flow = data as any as TelegramFlow;
    setInitialValues({
      ..._initialValues,
      ...data,
      defaultPayinDisputeTemplate: !flow?.payinDisputeTemplate, // TODO: fix typings?
      defaultPayinCancelTemplate: !flow?.payinCancelTemplate,
      defaultPayinCompleteTemplate: !flow?.payinCompleteTemplate,
      payinHideActions: !!flow?.payinHideActions,
      payinHideCancel: !!flow?.payinHideCancel,
      payinRequireReceipt: !!flow?.payinRequireReceipt,
      payinSendDisputesMessage: !!flow?.payinSendDisputesMessage,
    });
  }, [data, getInitialValues]);

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        const values = {
          chatId: result.data.values.chatId,
          enabled: result.data.values.enabled,
          payinDisputeTemplate: result.data.values.defaultPayinDisputeTemplate
            ? null
            : result.data.values.payinDisputeTemplate,
          payinCancelTemplate: result.data.values.defaultPayinCancelTemplate
            ? null
            : result.data.values.payinCancelTemplate,
          payinCompleteTemplate: result.data.values.defaultPayinCompleteTemplate
            ? null
            : result.data.values.payinCompleteTemplate,
          traderId: result.data.values.traderId || undefined,
          shopId: result.data.values.shopId || undefined,
          p2pProviderId: result.data.values.p2pProviderId || undefined,
          telegramUserId: result.data.values.telegramUserId || null,
          payinHideActions: result.data.values.payinHideActions,
          payinHideCancel: result.data.values.payinHideCancel,
          payinRequireReceipt: result.data.values.payinRequireReceipt,
          payinSendDisputesMessage: result.data.values.payinSendDisputesMessage,
        };
        onClose({
          ok: true,
          data: {
            ...result.data,
            values,
          },
        });
      } else {
        onClose(result);
      }
    },
    [onClose],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
      }}
      formikProps={{
        initialValues,
        enableReinitialize: true,
        validationSchema,
      }}
    >
      {({ formik }) => (
        <Form>
          <FormControls>
            <div>
              <FormikCheckbox name="enabled" label={t('fields.enabled')} />
              <FormikCheckbox
                name="payinSendDisputesMessage"
                label={t('fields.send_disputes_message')}
              />
              <div style={{ paddingLeft: hideActionsPaddingLeft }}>
                <FormikCheckbox
                  name="payinHideActions"
                  label={t('fields.payin_hide_actions')}
                  disabled={
                    context === TelegramFlowContext.Shop &&
                    !formik.values.payinSendDisputesMessage
                  }
                />
              </div>
              {(context === TelegramFlowContext.Trader ||
                context === TelegramFlowContext.P2PProvider) && (
                <div style={{ paddingLeft: DEPENDENT_CHECKBOX_INDENT }}>
                  <FormikCheckbox
                    name="payinHideCancel"
                    label={t('fields.payin_hide_cancel')}
                    disabled={formik.values.payinHideActions}
                  />
                </div>
              )}
              {context === TelegramFlowContext.Shop && (
                <FormikCheckbox
                  name="payinRequireReceipt"
                  label={t('fields.payin_require_receipt')}
                />
              )}
            </div>
            {context === TelegramFlowContext.Trader && (
              <TraderSelect name="traderId" label={t('fields.trader')} />
            )}
            {context === TelegramFlowContext.Shop && (
              <ShopSelect name="shopId" label={t('fields.shop')} />
            )}
            {context === TelegramFlowContext.P2PProvider && (
              <P2PProviderSelect
                name="p2pProviderId"
                label={t('fields.p2p_provider')}
                ignoreTradeMethodsIds
              />
            )}
            <FormikNumericTextField
              name="chatId"
              label={t('fields.chat_id')}
              required
            />
            {context === TelegramFlowContext.Shop && (
              <FormikSelect
                name="telegramUserId"
                label={t('fields.telegram_user')}
                helperText={t('fields.telegram_user_helper')}
                options={telegramUsersOptions}
                noneOption
              />
            )}
            <FormikCheckbox
              name="defaultPayinDisputeTemplate"
              label={t('fields.default_template')}
              helperText={
                formik.values.defaultPayinDisputeTemplate &&
                renderMultiline(t(DEFAULT_DISPUTE_TEMPLATE_HELPERS[context]))
              }
            />
            {!formik.values.defaultPayinDisputeTemplate && (
              <FormikTextField
                name="payinDisputeTemplate"
                label={t('fields.template')}
                helperText={
                  context === TelegramFlowContext.Shop
                    ? t('fields.template_shop_helper')
                    : t('fields.template_helper')
                }
                required
                multiline
                rows={3}
              />
            )}
            {context === TelegramFlowContext.Shop && (
              <Fragment>
                <FormikCheckbox
                  name="defaultPayinCancelTemplate"
                  label={t('fields.default_cancel_template')}
                  helperText={
                    formik.values.defaultPayinCancelTemplate &&
                    renderMultiline(t('fields.default_cancel_template_helper'))
                  }
                />
                {!formik.values.defaultPayinCancelTemplate && (
                  <FormikTextField
                    name="payinCancelTemplate"
                    label={t('fields.cancel_template')}
                    helperText={t('fields.reply_template_helper')}
                    required
                    multiline
                    rows={3}
                  />
                )}
                <FormikCheckbox
                  name="defaultPayinCompleteTemplate"
                  label={t('fields.default_complete_template')}
                  helperText={
                    formik.values.defaultPayinCompleteTemplate &&
                    renderMultiline(
                      t('fields.default_complete_template_helper'),
                    )
                  }
                />
                {!formik.values.defaultPayinCompleteTemplate && (
                  <FormikTextField
                    name="payinCompleteTemplate"
                    label={t('fields.complete_template')}
                    helperText={t('fields.reply_template_helper')}
                    required
                    multiline
                    rows={3}
                  />
                )}
              </Fragment>
            )}
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
